import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { selectUser } from '../../reducers/users/users.slice.ts'
import { inPov } from '../../utils/session.ts'
import { TABLE_HEIGHT_WITH_TRIAL_AND_PAGES, TABLE_HEIGHT_WITHOUT_TRIAL_AND_PAGES } from '../../strings/consts.ts'
import { getMembersForUserGroup, getPoliciesForUserGroup, getUserGroup } from '../../reducers/groups/groups.thunk.ts'
import { selectGroup, setUserGroup } from '../../reducers/groups/groups.slice.ts'
import DataGrid from '../data-grid/DataGrid.tsx'
import { policyGroupColumns } from './PolicyGroupColumns.tsx'
import { memberGroupColumns } from './MemberGroupColumns.tsx'
import { Pagination } from '../pagination/Pagination.tsx'
import { PaginationSelectOption } from '../../types/MemberTypes.ts'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../strings/api-consts.ts'
import { Button } from '@radix-ui/themes'
import ManagePoliciesPopout from './ManagePoliciesPopout.tsx'
import { IconListCheck, IconUser } from '@tabler/icons-react'
import PageSkeleton from '../skeleton/PageSkeleton.tsx'

function Group() {
  const { session } = useSelector(selectUser)
  const {
    userGroupMembers,
    userGroupPolicies,
    totalPagesMemberGroups,
    totalPagesPolicyGroups,
    currentPageMemberGroups,
    currentPagePolicyGroups,
  } = useSelector(selectGroup)
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { groupId } = useParams()
  const [selectedPage, setSelectedPage] = useState<string>('members')
  const [showManagePopout, setShowManagePopout] = useState<boolean>(false)

  useEffect(() => {
    const session = localStorage.getItem('session') as string

    if (session === '{}') {
      navigate('/login')
    }

    return () => {
      dispatch(setUserGroup(undefined))
    }
  }, [])

  useEffect(() => {
    if (groupId) {
      di(getUserGroup(Number(groupId)))
      di(getMembersForUserGroup(Number(groupId), DEFAULT_PAGE, DEFAULT_PAGE_SIZE))
      di(getPoliciesForUserGroup(Number(groupId), DEFAULT_PAGE, DEFAULT_PAGE_SIZE))
    }
  }, [groupId])

  const navigateToPolicy = (policyId: number) => {
    navigate(`/device-policies/${policyId}`)
  }

  const handleMemberPageClick = (data: PaginationSelectOption) => {
    di(getMembersForUserGroup(Number(groupId), data.selected + 1, DEFAULT_PAGE_SIZE))
  }

  const handlePolicyPageClick = (data: PaginationSelectOption) => {
    di(getPoliciesForUserGroup(Number(groupId), data.selected + 1, DEFAULT_PAGE_SIZE))
  }

  const reloadGroupPolicies = () => {
    di(getPoliciesForUserGroup(Number(groupId), DEFAULT_PAGE, DEFAULT_PAGE_SIZE))
  }

  return (
    <>
      <div className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l bg-contentBackground w-[calc(100%_-_208px)] h-[calc(100%_-_74px)] overflow-auto">
        <div className="h-12 w-full border-b border-mainBorder flex gap-6 p-4 mb-2 bg-white sticky top-0 z-10">
          <div>
            <p className="text-mainText text-sm cursor-pointer" onClick={() => setSelectedPage('members')}>
              Members
            </p>
            {selectedPage === 'members' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
          </div>

          <div>
            <p className="text-mainText text-sm cursor-pointer" onClick={() => setSelectedPage('policies')}>
              Policies
            </p>
            {selectedPage === 'policies' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
          </div>

          {selectedPage === 'policies' && (
            <Button
              className="h-[34px] bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple ml-auto cursor-pointer -mt-2.5"
              onClick={() => setShowManagePopout(true)}
            >
              Manage Policies
            </Button>
          )}
        </div>
        <div className="px-2">
          {selectedPage === 'members' && (
            <>
              {!userGroupMembers && <PageSkeleton />}
              {userGroupMembers && userGroupMembers.length > 0 && (
                <>
                  <DataGrid
                    columns={memberGroupColumns()}
                    data={userGroupMembers}
                    loading={userGroupMembers === undefined}
                    classNames={
                      inPov(session)
                        ? `calc(100% - ${TABLE_HEIGHT_WITH_TRIAL_AND_PAGES})`
                        : `calc(100% - ${TABLE_HEIGHT_WITHOUT_TRIAL_AND_PAGES})`
                    }
                  />
                  <div className="mt-2">
                    <Pagination
                      totalPages={totalPagesMemberGroups}
                      forcePage={currentPageMemberGroups}
                      handlePageClick={handleMemberPageClick}
                    />
                  </div>
                </>
              )}
              {userGroupMembers && userGroupMembers.length === 0 && (
                <div className="block items-center mt-20">
                  <div className="flex items-center justify-center">
                    <IconUser className="h-12 w-12" />
                  </div>
                  <p className="text-center font-bold mt-4">No members assigned</p>
                  <p className="text-center text-sm mx-auto max-w-[300px]">
                    Members can only be assigned to this group from your configured SCIM provider
                  </p>
                </div>
              )}
            </>
          )}
          {selectedPage === 'policies' && (
            <>
              {!userGroupPolicies && <PageSkeleton />}
              {userGroupPolicies && userGroupPolicies.length > 0 && (
                <>
                  <DataGrid
                    columns={policyGroupColumns(navigateToPolicy)}
                    data={userGroupPolicies}
                    loading={userGroupPolicies === undefined}
                    classNames={
                      inPov(session)
                        ? `calc(100% - ${TABLE_HEIGHT_WITH_TRIAL_AND_PAGES})`
                        : `calc(100% - ${TABLE_HEIGHT_WITHOUT_TRIAL_AND_PAGES})`
                    }
                  />
                  <div className="mt-2">
                    <Pagination
                      totalPages={totalPagesPolicyGroups}
                      forcePage={currentPagePolicyGroups}
                      handlePageClick={handlePolicyPageClick}
                    />
                  </div>
                </>
              )}
              {userGroupPolicies && userGroupPolicies.length === 0 && (
                <div className="block items-center mt-20">
                  <div className="flex items-center justify-center">
                    <IconListCheck className="h-12 w-12" />
                  </div>
                  <p className="text-center font-bold mt-4">No policies selected</p>
                  <p className="text-center text-sm mx-auto max-w-[300px]">
                    Select the required policies for this group of members, if no policies exist navigate to{' '}
                    <a
                      onClick={() => {
                        navigate('/device-policies')
                      }}
                      className="underline cursor-pointer"
                    >
                      Policies
                    </a>{' '}
                    to get started.
                  </p>
                  <Button
                    className="h-[34px] flex bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple mx-auto cursor-pointer mt-4"
                    onClick={() => setShowManagePopout(true)}
                  >
                    Select Policies
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showManagePopout && groupId && (
        <ManagePoliciesPopout
          setShowPopout={setShowManagePopout}
          groupId={groupId}
          reloadGroupPolicies={reloadGroupPolicies}
        />
      )}
    </>
  )
}

export default Group
